import { useState, useEffect } from "react";
import styles from "./Purchaseform.module.css";
import { ArrowLeft, Send, CheckCircle, Minus, Plus } from "lucide-react";
import { api } from "../../store/slices/authSlice";
import { useTranslation } from "react-i18next";

const INITIAL = {
  country_id: "",
  governorate_id: "",
  city_id: "",
  quantity: 1,
  recipient_phone: "",
  distinguishing_mark: "",
};

export default function PurchaseForm({ product, onBack, onClose }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [countries, setCountries] = useState([]);
  const [governorates, setGovernorates] = useState([]);
  const [cities, setCities] = useState([]);

  const [loadingGov, setLoadingGov] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);

  const maxQty = product?.quantity_type?.available_quantity ?? null;
  const hasStockLimit = maxQty !== null;

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/countries");
        if (res.data.code === 200) setCountries(res.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    if (!form.country_id) {
      setGovernorates([]);
      setCities([]);
      return;
    }
    const fetch = async () => {
      setLoadingGov(true);
      try {
        const res = await api.get(`/governorates/${form.country_id}`);
        if (res.data.code === 200) setGovernorates(res.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingGov(false);
      }
    };
    fetch();
    setForm((prev) => ({ ...prev, governorate_id: "", city_id: "" }));
    setCities([]);
  }, [form.country_id]);

  useEffect(() => {
    if (!form.governorate_id) {
      setCities([]);
      return;
    }
    const fetch = async () => {
      setLoadingCity(true);
      try {
        const res = await api.get(`/cities/${form.governorate_id}`);
        if (res.data.code === 200) setCities(res.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingCity(false);
      }
    };
    fetch();
    setForm((prev) => ({ ...prev, city_id: "" }));
  }, [form.governorate_id]);

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const incrementQty = () => {
    setForm((prev) => {
      const current = Number(prev.quantity) || 0;
      const next = hasStockLimit ? Math.min(current + 1, maxQty) : current + 1;
      return { ...prev, quantity: next };
    });
    setErrors((prev) => ({ ...prev, quantity: "" }));
  };

  const decrementQty = () => {
    setForm((prev) => {
      const current = Number(prev.quantity) || 1;
      const next = Math.max(current - 1, 1);
      return { ...prev, quantity: next };
    });
    setErrors((prev) => ({ ...prev, quantity: "" }));
  };

  const handleQtyInput = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setForm((prev) => ({ ...prev, quantity: "" }));
      return;
    }
    let val = Number(raw);
    if (isNaN(val)) return;
    if (val < 1) val = 1;
    if (hasStockLimit && val > maxQty) val = maxQty;
    setForm((prev) => ({ ...prev, quantity: val }));
    setErrors((prev) => ({ ...prev, quantity: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.country_id) errs.country_id = t("errors.required");
    if (!form.governorate_id) errs.governorate_id = t("errors.required");
    if (!form.city_id) errs.city_id = t("errors.required");

    const qty = Number(form.quantity);
    if (!form.quantity || isNaN(qty) || qty < 1) {
      errs.quantity = t("errors.invalidQuantity");
    } else if (hasStockLimit && qty > maxQty) {
      errs.quantity = t("errors.exceedsStock", { max: maxQty });
    }

    if (!form.recipient_phone) errs.recipient_phone = t("errors.required");
    if (!form.distinguishing_mark)
      errs.distinguishing_mark = t("errors.required");
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        supplier_material_id: product.id,
        country_id: Number(form.country_id),
        governorate_id: Number(form.governorate_id),
        city_id: Number(form.city_id),
        quantity: Number(form.quantity),
        recipient_phone: form.recipient_phone,
        distinguishing_mark: form.distinguishing_mark,
      };
      const res = await api.post(
        "/supplier-materials/purchase-orders",
        payload,
      );
      if (res.data.code === 201 || res.data.code === 200) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error(err);
      setErrors({
        submit: err.response?.data?.message || t("errors.somethingWrong"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className={styles.success} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.successIcon}>
          <CheckCircle size={36} />
        </div>
        <h3 className={styles.successTitle}>{t("purchase.successTitle")}</h3>
        <p className={styles.successSub}>
          {t("purchase.successMsg", { name: product.name })}
        </p>
        <button className={styles.closeBtn} onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.formWrap} dir={isRtl ? "rtl" : "ltr"}>
      <button className={styles.backBtn} onClick={onBack}>
        <ArrowLeft
          size={15}
          style={{ transform: isRtl ? "rotate(180deg)" : "none" }}
        />
        {t("purchase.back")}
      </button>

      <h3 className={styles.formTitle}>{t("purchase.welcome")}</h3>
      <p className={styles.formSub}>{t("purchase.welcomeSub")}</p>

      <div className={styles.field}>
        <label>{t("purchase.country")}</label>
        <select
          value={form.country_id}
          onChange={set("country_id")}
          className={errors.country_id ? styles.hasError : ""}
        >
          <option value="">{t("purchase.selectCountry")}</option>
          {countries.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {errors.country_id && (
          <span className={styles.error}>{errors.country_id}</span>
        )}
      </div>

      <div className={styles.field}>
        <label>{t("purchase.governorate")}</label>
        <select
          value={form.governorate_id}
          onChange={set("governorate_id")}
          disabled={!form.country_id || loadingGov}
          className={errors.governorate_id ? styles.hasError : ""}
        >
          <option value="">
            {loadingGov ? t("common.loading") : t("purchase.selectGov")}
          </option>
          {governorates.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
        {errors.governorate_id && (
          <span className={styles.error}>{errors.governorate_id}</span>
        )}
      </div>

      <div className={styles.field}>
        <label>{t("purchase.city")}</label>
        <select
          value={form.city_id}
          onChange={set("city_id")}
          disabled={!form.governorate_id || loadingCity}
          className={errors.city_id ? styles.hasError : ""}
        >
          <option value="">
            {loadingCity ? t("common.loading") : t("purchase.selectCity")}
          </option>
          {cities.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {errors.city_id && (
          <span className={styles.error}>{errors.city_id}</span>
        )}
      </div>

      <div className={styles.field}>
        <label>
          {t("purchase.quantity")}
          {hasStockLimit && (
            <span className={styles.stockHint}>
              {" "}
              ({t("purchase.available")}: {maxQty})
            </span>
          )}
        </label>
        <div
          className={`${styles.qtyCounter} ${errors.quantity ? styles.hasError : ""}`}
        >
          <button
            type="button"
            onClick={decrementQty}
            disabled={Number(form.quantity) <= 1}
            className={styles.qtyBtn}
            aria-label="decrease"
          >
            <Minus size={16} />
          </button>
          <input
            type="number"
            value={form.quantity}
            onChange={handleQtyInput}
            min={1}
            max={hasStockLimit ? maxQty : undefined}
            className={styles.qtyInput}
          />
          <button
            type="button"
            onClick={incrementQty}
            disabled={hasStockLimit && Number(form.quantity) >= maxQty}
            className={styles.qtyBtn}
            aria-label="increase"
          >
            <Plus size={16} />
          </button>
        </div>
        {errors.quantity && (
          <span className={styles.error}>{errors.quantity}</span>
        )}
      </div>

      <div className={styles.field}>
        <label>{t("purchase.phone")}</label>
        <input
          type="tel"
          placeholder="+20 1xx xxx xxxx"
          value={form.recipient_phone}
          onChange={set("recipient_phone")}
          className={errors.recipient_phone ? styles.hasError : ""}
        />
        {errors.recipient_phone && (
          <span className={styles.error}>{errors.recipient_phone}</span>
        )}
      </div>

      <div className={styles.field}>
        <label>{t("purchase.detailedArea")}</label>
        <textarea
          placeholder={t("purchase.areaPlaceholder")}
          value={form.distinguishing_mark}
          onChange={set("distinguishing_mark")}
          className={errors.distinguishing_mark ? styles.hasError : ""}
        />
        {errors.distinguishing_mark && (
          <span className={styles.error}>{errors.distinguishing_mark}</span>
        )}
      </div>

      {errors.submit && <p className={styles.submitError}>{errors.submit}</p>}

      <button
        className={styles.submitBtn}
        onClick={handleSubmit}
        disabled={submitting}
      >
        <Send size={16} />
        {submitting ? t("common.sending") : t("purchase.submitBtn")}
      </button>
    </div>
  );
}
